<template>
  <div class="panel movimiento-panel">
    <h3>Alertas de Movimientos Drásticos de la Competencia
      <span class="total-badge">{{ alertas.length }} movimientos</span>
    </h3>

    <div v-if="alertas.length > 0" class="tiendas-list">
      <div v-for="(items, tienda) in agrupadoPorTienda" :key="tienda" class="tienda-row" @click="abrirModal(tienda, items)">
        <div class="tienda-info">
          <span class="tienda-nombre">{{ formatStoreName(tienda) }}</span>
          <span class="tienda-count">{{ items.length }} bajadas — promedio {{ promedioCaida(items) }}%</span>
        </div>
        <button class="ver-btn">Ver detalle →</button>
      </div>
    </div>

    <div v-else class="no-alerts-message">
      Sin movimientos drásticos en el período analizado.
    </div>

    <!-- Modal de lista de bajadas por tienda -->
    <div v-if="modalVisible" class="modal-overlay" @click.self="cerrarModal">
      <div class="modal-content">
        <div class="modal-header">
          <h4>{{ formatStoreName(modalTienda) }} — {{ modalItems.length }} bajadas de precio</h4>
          <div class="modal-actions">
            <button @click="exportarExcel" class="export-btn"><PhChartBar :size="13" weight="bold" /> EXCEL</button>
            <button @click="exportarCsv" class="export-btn export-btn-csv"><PhDownload :size="13" weight="bold" /> CSV</button>
            <button @click="cerrarModal" class="close-btn"><PhX :size="14" weight="bold" /></button>
          </div>
        </div>
        <div class="modal-body">
          <table class="modal-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th>Precio Anterior</th>
                <th>Precio Actual</th>
                <th>Caída</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in modalItemsOrdenados" :key="item.codigo">
                <td class="code-cell">
                  <span class="codigo-link" @click="abrirDetalle(item.codigo, item.descripcion)">
                    [{{ item.codigo }}]
                  </span>
                </td>
                <td>{{ item.descripcion }}</td>
                <td class="price-old">${{ item.precio_anterior }}</td>
                <td class="price-new">
                  <a v-if="item.url_competidor && !item.url_competidor.includes('lista-txt')" :href="item.url_competidor" target="_blank" class="price-link">${{ item.precio_actual }}</a>
                  <span v-else>${{ item.precio_actual }}</span>
                </td>
                <td class="gap-cell">-{{ item.caida_porcentaje }}%</td>
                <td>
                  <button v-if="esSuperadmin" class="corregir-small" @click="marcarCorreccion(item.codigo)"><PhWarning :size="13" weight="bold" /></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal de detalle de producto (compartido) -->
    <ModalProducto
      v-model:visible="detalleVisible"
      :codigo="detalleCodigo"
      :descripcion="detalleDescripcion"
      :initial-vista="vista"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useCorreccion } from '../composables/useCorreccion.js';
import ModalProducto from './ModalProducto.vue';
import { exportarAExcel, exportarACsv } from '../composables/useExcelExport';
import { PhChartBar, PhDownload, PhX, PhWarning } from '@phosphor-icons/vue'

const { esSuperadmin, marcarCorreccion } = useCorreccion();
const props = defineProps({
  alertas: Array,
  vista: { type: String, default: 'web' }
});
defineEmits(['verProducto']);

// Modal de detalle (componente compartido)
const detalleVisible = ref(false);
const detalleCodigo = ref('');
const detalleDescripcion = ref('');

const abrirDetalle = (codigo, descripcion) => {
  detalleCodigo.value = codigo;
  detalleDescripcion.value = descripcion || '';
  detalleVisible.value = true;
};

// Modal de lista por tienda
const modalVisible = ref(false);
const modalTienda = ref('');
const modalItems = ref([]);

const formatStoreName = (name) => {
  if (!name) return '';
  return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const promedioCaida = (items) => {
  const avg = items.reduce((sum, i) => sum + i.caida_porcentaje, 0) / items.length;
  return Math.round(avg * 10) / 10;
};

const agrupadoPorTienda = computed(() => {
  const grupos = {};
  for (const alerta of props.alertas) {
    const key = alerta.competidor;
    if (!grupos[key]) grupos[key] = [];
    grupos[key].push(alerta);
  }
  return Object.fromEntries(
    Object.entries(grupos).sort((a, b) => b[1].length - a[1].length)
  );
});

const modalItemsOrdenados = computed(() =>
  [...modalItems.value].sort((a, b) => b.caida_porcentaje - a.caida_porcentaje)
);

const abrirModal = (tienda, items) => {
  modalTienda.value = tienda;
  modalItems.value = items;
  modalVisible.value = true;
};

const cerrarModal = () => { modalVisible.value = false; };

const _buildRowsMovim = () => {
  const headers = ['Código', 'Producto', 'Precio Anterior', 'Precio Actual', 'Caída %', 'Competidor'];
  const rows = modalItemsOrdenados.value.map(item => [
    item.codigo, item.descripcion, item.precio_anterior,
    item.precio_actual, item.caida_porcentaje, formatStoreName(modalTienda.value)
  ]);
  return { headers, rows };
};

const exportarExcel = () => {
  const { headers, rows } = _buildRowsMovim();
  exportarAExcel({
    headers, rows,
    priceColumns: [2, 3],
    filename: `movimiento_drastico_${modalTienda.value}_${new Date().toISOString().slice(0,10)}.xlsx`,
    sheetName: 'Movimiento Drástico'
  });
};

const exportarCsv = () => {
  const { headers, rows } = _buildRowsMovim();
  exportarACsv({
    headers, rows,
    filename: `movimiento_drastico_${modalTienda.value}_${new Date().toISOString().slice(0,10)}.csv`
  });
};
</script>

<style scoped>
/* ==================== HEADER ==================== */
.total-badge {
  font-family: var(--font-mono);
  font-size: 10.5px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  border-radius: 3px;
  padding: 2px 8px;
  margin-left: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
}

/* ==================== LISTA DE TIENDAS ==================== */
.tiendas-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}
.tienda-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: var(--color-surface-2);
  border-left: 3px solid var(--color-accent);
  border-top: 1px solid var(--color-border);
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.15s;
}
.tienda-row:hover {
  background: var(--color-hover-row);
  border-color: var(--color-border-strong);
  transform: translateX(2px);
}
.tienda-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.tienda-nombre {
  font-weight: 600;
  font-size: 13px;
  color: var(--color-text);
  letter-spacing: 0.01em;
}
.tienda-count {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
}
.ver-btn {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
  padding: 5px 12px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 10.5px;
  font-family: var(--font-mono);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.ver-btn:hover {
  background: var(--color-accent);
  color: var(--color-surface-3);
}

/* ==================== MODAL TERMINAL ==================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: var(--color-surface);
  border: 1px solid var(--color-accent-border);
  border-radius: 3px;
  width: 90%;
  max-width: 900px;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
}
.modal-header h4 {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.modal-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}
.export-btn {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  padding: 4px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.export-btn:hover {
  color: var(--color-success);
  border-color: var(--color-success);
}
.export-btn-csv:hover {
  color: var(--color-text);
  border-color: var(--color-text-muted);
}
.close-btn {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  padding: 4px 8px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 11px;
  font-family: var(--font-mono);
  transition: all 0.15s;
}
.close-btn:hover {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
.modal-body {
  overflow-y: auto;
  padding: 12px 16px 16px;
}

/* ==================== TABLA MODAL ==================== */
.modal-table {
  width: 100%;
  border-collapse: collapse;
}
.modal-table th {
  text-align: left;
  padding: 6px 8px;
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  border-bottom: 1px solid var(--color-border-strong);
  background: var(--color-surface-3);
  position: sticky;
  top: 0;
}
.modal-table td {
  padding: 7px 8px;
  font-size: 11.5px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
}
.modal-table tbody tr:hover {
  background: var(--color-hover-row);
}
.code-cell {
  font-family: var(--font-mono);
  color: var(--color-text-faint);
  font-size: 10.5px;
}
.codigo-link {
  cursor: pointer;
  color: var(--color-accent);
  font-family: var(--font-mono);
  transition: color 0.1s;
}
.codigo-link:hover {
  color: var(--color-accent-strong);
  text-decoration: underline;
}
.price-cell, .price-link {
  color: var(--color-success-text);
  font-weight: 600;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  text-decoration: none;
}
.price-link:hover {
  text-decoration: underline;
}
.gap-cell, .gap-link {
  color: var(--color-danger-text);
  font-weight: 700;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  text-decoration: none;
}
.gap-link:hover {
  text-decoration: underline;
}
.price-old {
  opacity: 0.45;
  text-decoration: line-through;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}
.price-new {
  color: var(--color-success-text);
  font-weight: 600;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}
.corregir-small {
  background: transparent;
  color: var(--color-warning);
  border: 1px solid var(--color-warning-border);
  padding: 2px 6px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 10px;
  margin-left: 4px;
  transition: all 0.1s;
}
.corregir-small:hover {
  background: var(--color-warning-bg);
}

/* ==================== MENSAJE NO ALERTAS ==================== */
.no-alerts-message {
  padding: 16px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 12px;
  font-style: italic;
  background: var(--color-surface-2);
  border: 1px dashed var(--color-border-strong);
  border-radius: 2px;
  margin-top: 12px;
}
</style>